import classNames from 'classnames';
import { useField } from 'formik';
import type React from 'react';
import { forwardRef } from 'react';
import { TextCaption } from '../../Typography';

const INPUT_TYPE = 'text';

export interface InputNumberFieldProps {
  size?: string;
  name: string;
  placeholder?: string;
  label?: string;
  noAddedHeight?: boolean;
  state?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  autoComplete?: 'on' | 'off';
}

const State = {
  normal: 'border-ds-natural-100',
  warning: 'border-ds-secondary-400',
  success: 'border-ds-accent2-400',
  error: 'border-ds-danger-400',
  invisible: 'border-none',
  dark: 'border-ds-natural-150',
};

enum allowedKeys {
  Backspace = 'Backspace',
  ArrowRight = 'ArrowRight',
  ArrowLeft = 'ArrowLeft',
  Tab = 'Tab',
}
export const inputClasses = `
  rounded-md
  placeholder-ds-natural-300
  focus:placeholder-transparent
  leading-ds-300
  outline-none
  border
  box-border
  border-solid
  border-ds-natural-150
  disabled:bg-ds-natural-200-a
  disabled:text-ds-natural-200
  disabled:border-ds-natural-200
  focus:outline-none
  focus:ring-0
  w-full
  tracking-ds-normal
  font-ds-inter
  h-ds-md-input py-1 text-ds-400 font-ds-normal
  `;

export const InputNumberField = forwardRef(
  (
    {
      state = 'normal',
      name,
      onChange,
      placeholder = '',
      label,
      noAddedHeight,
      autoComplete = 'on',
    }: InputNumberFieldProps,
    _ref
  ) => {
    const [field, meta] = useField(name);
    const hasError = meta.error;
    return (
      <div
        className={classNames('flex flex-col relative w-full', {
          'h-20': !noAddedHeight,
        })}
      >
        <input
          autoComplete={autoComplete}
          placeholder={placeholder}
          value={field?.value || ''}
          onChange={(e) => {
            onChange?.(e);
            field.onChange(e);
          }}
          onKeyDown={(event) => {
            if (!/^[0-9]$/i.test(event.key) && !allowedKeys[event.key]) {
              event.preventDefault();
            }
          }}
          name={name}
          className={classNames([
            inputClasses,
            { color: 'blue;' },
            State[state],
            state === 'dark'
              ? 'text-ds-natural-50 bg-transparent focus:border-ds-accent3-100'
              : 'text-ds-natural-600 bg-ds-accent3-100 focus:border-ds-primary',
          ])}
          type={INPUT_TYPE}
        />
        {hasError && (
          <div
            className={classNames('mx-1 absolute w-full', {
              'top-7': !label,
              'top-55': label,
            })}
          >
            {hasError && (
              <div className="whitespace-nowrap">
                <TextCaption
                  label={meta.error ?? ''}
                  color="text-ds-danger-400"
                  lineHeight="leading-ds-100"
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

InputNumberField.displayName = 'InputNumberField';
